import React from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import "../styles/raci-matrix.css";
import SideTabs from "../components/layout/SideTabs";
import ValueAddedPanel from "../components/ui/ValueAddedPanel";
import PerformancePanel from "../components/ui/PerformancePanel";
import FlowAnalysisPanel from "../components/ui/FlowAnalysisPanel";
import RedesignScenariosPanel from "../components/ui/RedesignScenariosPanel";
import JobCapiPanel from "../components/ui/JobCapiPanel";
import WorkflowJobChips, {
  RaciLegendBar,
  mapJobTasksToWorkflowJobs,
} from "../components/ui/WorkflowJobChips";
import { useAppContext } from "../context/AppContext";
import {
  getProcessPaths,
  getProcessWithRelations,
  getProcessesByCompanyId,
} from "../services/processService";
import { getKPIsByProcessId } from "../services/kpiService";
import RichTextEditor from "../components/ui/RichTextEditor";
import { toast } from "../utils/toast";
import { useLocation, useParams } from "react-router-dom";

// ── Gateway type metadata (mirrors GatewaySection & GatewayConfigModal) ──────
const GATEWAY_TYPE_INFO = {
  EXCLUSIVE:   { symbol: "✕", color: "#ef4444", bg: "#fef2f2", border: "#fecaca", label: "Exclusive" },
  PARALLEL:    { symbol: "+", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0", label: "Parallel"  },
  INCLUSIVE:   { symbol: "○", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", label: "Inclusive" },
  EVENT_BASED: { symbol: "⬡", color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe", label: "Event-Based" },
  COMPLEX:     { symbol: "✱", color: "#9b59b6", bg: "#f5f3ff", border: "#e9d5ff", label: "Complex"  },
};
const getGatewayTypeInfo = (type) =>
  GATEWAY_TYPE_INFO[(type || "exclusive").toUpperCase()] || GATEWAY_TYPE_INFO.EXCLUSIVE;

// Inline SVG: task / checklist icon (matches BPMN task node aesthetic)
const TaskIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0 }}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <line x1="9" y1="9" x2="15" y2="9"/>
    <line x1="9" y1="12" x2="15" y2="12"/>
    <line x1="9" y1="15" x2="13" y2="15"/>
  </svg>
);

const isPathSubProcessNode = (node) => {
  if (!node || typeof node !== "object") return false;
  const kind = String(node.kind || "").toLowerCase();
  if (
    kind.includes("subprocess") ||
    kind.includes("sub_process") ||
    kind.includes("child_process") ||
    kind.includes("callactivity") ||
    kind.includes("call_activity")
  ) {
    return true;
  }
  if (
    node.isSubProcess ||
    node.is_sub_process ||
    node.isChildProcess ||
    node.is_child_process ||
    node.child_process_id != null ||
    node.childProcessId != null
  ) {
    return true;
  }
  const name = String(node.name || node.targetTaskName || node.joinTaskName || "");
  return /\[\s*sub[-\s]?process\s*\]/i.test(name) || /\(sub[-\s]?process\)/i.test(name);
};

const getPathNodeDisplayName = (node) => {
  const raw = getPathNodeName(node);
  return raw
    .replace(/^\s*\[\s*sub[-\s]?process\s*\]\s*/i, "")
    .replace(/\s*\(\s*sub[-\s]?process\s*\)\s*$/i, "")
    .trim() || raw;
};

const decodePathText = (value) => {
  const raw = String(value ?? "").replace(/&#10;|&NewLine;/g, "\n");
  if (typeof document === "undefined") return raw;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = raw;
  return textarea.value;
};

/**
 * Flattens the NEW backend path format into a flat node array compatible with
 * the existing diagram renderer.
 *
 * New format: each raw path = [ inclusiveGatewayNode ] where the root node has
 *   { kind: "inclusive", id, name, activeBranches: [{ condition, targetId, path: [task|exclusive, ...] }] }
 *
 * Old format (still accepted): each raw path is already a flat array of nodes.
 *
 * Returns a flat array of nodes:
 *   [ inclusiveGatewayNode, ...branchNodes from all activeBranches ]
 * Gateway nodes carry a synthesised `condition` string for the diagram.
 */
const flattenNewStylePath = (rawPath) => {
  if (!Array.isArray(rawPath) || rawPath.length === 0) return [];

  const seen = new Set();
  const flatNodes = [];

  const processNode = (node, defaultCondition = "") => {
    if (!node || typeof node !== "object") return;

    const kind = String(node.kind || "").toLowerCase();
    const isGateway =
      kind.includes("inclusive") ||
      kind.includes("exclusive") ||
      kind.includes("parallel");

    const activeBranches = Array.isArray(node.activeBranches)
      ? node.activeBranches
      : null;
    const branches = Array.isArray(node.branches) ? node.branches : null;

    let synthesisedCondition = decodePathText(node.condition || defaultCondition);
    if (!synthesisedCondition && isGateway) {
      if (activeBranches) {
        synthesisedCondition = activeBranches
          .map((b) => decodePathText(b?.condition || ""))
          .filter(Boolean)
          .join(" + ");
      } else if (Array.isArray(node.conditions)) {
        synthesisedCondition = node.conditions
          .map((c) => decodePathText(c || ""))
          .filter(Boolean)
          .join(" + ");
      } else if (branches) {
        synthesisedCondition = branches
          .map((b) =>
            decodePathText(
              (typeof b === "object" && b && !Array.isArray(b) ? b.condition : "") || ""
            )
          )
          .filter(Boolean)
          .join(" + ");
      }
    }

    const nodeKey = getPathNodeKey(node);
    if (!seen.has(nodeKey)) {
      seen.add(nodeKey);
      flatNodes.push(
        synthesisedCondition && synthesisedCondition !== node.condition
          ? { ...node, condition: synthesisedCondition }
          : node
      );
    }

    if (activeBranches) {
      activeBranches.forEach((b) => {
        const branchPath = Array.isArray(b?.path)
          ? b.path
          : Array.isArray(b?.nodes)
          ? b.nodes
          : [];
        const cond = decodePathText(b?.condition || "");
        branchPath.forEach((child) => processNode(child, cond));
      });
    } else if (branches) {
      branches.forEach((b, idx) => {
        const cond = decodePathText(
          (typeof b === "object" && b && !Array.isArray(b) ? b.condition : "") ||
            (Array.isArray(node.conditions) ? node.conditions[idx] : "") ||
            ""
        );
        if (Array.isArray(b)) {
          b.forEach((child) => processNode(child, cond));
        } else if (b && typeof b === "object") {
          const branchPath = Array.isArray(b.nodes)
            ? b.nodes
            : Array.isArray(b.path)
            ? b.path
            : [];
          branchPath.forEach((child) => processNode(child, cond));
        }
      });
    }
  };

  rawPath.forEach((n) => processNode(n));
  return flatNodes;
};

/**
 * Structure-preserving counterpart to flattenNewStylePath.
 *
 * Instead of collapsing every branch into one flat sequence, this keeps the
 * branch grouping so the diagram can lay branches out side-by-side. Each gateway
 * node gets a normalised `branches` array of `{ condition, targetId, nodes }`,
 * where `nodes` is the ordered (and recursively structured) list of steps that
 * run inside that branch.
 */
const structurePathNode = (node) => {
  if (!node || typeof node !== "object") return node;

  // New backend shape: gateway carries `activeBranches`.
  if (Array.isArray(node.activeBranches)) {
    return {
      ...node,
      branches: node.activeBranches.map((b) => ({
        condition: decodePathText(b?.condition || ""),
        targetId: b?.targetId ?? b?.target_id ?? null,
        nodes: (Array.isArray(b?.path)
          ? b.path
          : Array.isArray(b?.nodes)
          ? b.nodes
          : []
        ).map(structurePathNode),
      })),
    };
  }

  // Already-structured shape or `branches` array format
  if (Array.isArray(node.branches)) {
    return {
      ...node,
      branches: node.branches.map((b, idx) => {
        if (Array.isArray(b)) {
          const cond =
            (Array.isArray(node.conditions) ? node.conditions[idx] : "") || "";
          return {
            condition: decodePathText(cond),
            targetId: null,
            nodes: b.map(structurePathNode),
          };
        }
        const cond =
          b?.condition ||
          (Array.isArray(node.conditions) ? node.conditions[idx] : "") ||
          "";
        return {
          condition: decodePathText(cond),
          targetId: b?.targetId ?? b?.target_id ?? null,
          nodes: (Array.isArray(b?.nodes)
            ? b.nodes
            : Array.isArray(b?.path)
            ? b.path
            : []
          ).map(structurePathNode),
        };
      }),
    };
  }

  return node;
};

const structurePathEntry = (entry) => {
  if (Array.isArray(entry)) return entry.map(structurePathNode);
  return [entry].filter(Boolean).map(structurePathNode);
};

const normalizePathPayload = (payload) => {
  const body =
    payload && typeof payload === "object" && payload.data ? payload.data : payload;
  const rawPaths = Array.isArray(body) ? body : Array.isArray(body?.paths) ? body.paths : [];
  // Each element in rawPaths may itself be wrapped in an outer array (new format) or
  // already be a flat node array (old format).
  const paths = rawPaths.map((entry) => {
    // New format: paths[i] = [ inclusiveNode ] — an array containing the root gateway
    if (Array.isArray(entry)) {
      return flattenNewStylePath(entry);
    }
    // Fallback: treat as a single node
    return flattenNewStylePath([entry].filter(Boolean));
  });
  // Branch-preserving view used by the diagram to render side-by-side branches.
  const graphPaths = rawPaths.map(structurePathEntry);
  return {
    start: body?.start ?? "",
    totalPaths: Number(body?.totalPaths ?? paths.length) || 0,
    maxPaths: Number(body?.maxPaths ?? 0) || 0,
    paths,
    graphPaths,
  };
};

const getPathNodeName = (node) =>
  decodePathText(node?.name || node?.targetTaskName || node?.joinTaskName || "Unnamed step");

const isTaskNode = (node) => {
  if (!node || typeof node !== "object") return false;
  if (isPathSubProcessNode(node)) return false;
  const kind = String(node?.kind || "").toLowerCase();
  if (
    kind.includes("inclusive") ||
    kind.includes("exclusive") ||
    kind.includes("parallel") ||
    kind.includes("gateway") ||
    kind.includes("complex") ||
    kind.includes("event_based")
  ) {
    return false;
  }
  if (
    kind === "start" ||
    kind === "end" ||
    kind.includes("start_event") ||
    kind.includes("end_event") ||
    kind.includes("startevent") ||
    kind.includes("endevent") ||
    kind === "__path_start__" ||
    kind === "__path_end__"
  ) {
    return false;
  }
  return true;
};

const getPathStartEnd = (path = []) => {
  const isWorkStep = (node) => isTaskNode(node) || isPathSubProcessNode(node);
  const isEndEvent = (node) => {
    const kind = String(node?.kind || "").toLowerCase();
    return kind.includes("end");
  };
  const firstTask = path.find(isWorkStep);
  const lastEndEvent = [...path].reverse().find(isEndEvent);
  const lastTask = [...path].reverse().find(isWorkStep);

  const startName = firstTask ? getPathNodeDisplayName(firstTask) : "Start";
  let endName = "End";
  if (lastTask && lastEndEvent) {
    const endEventIdx = path.indexOf(lastEndEvent);
    const lastTaskIdx = path.indexOf(lastTask);
    endName =
      endEventIdx >= lastTaskIdx
        ? getPathNodeDisplayName(lastEndEvent)
        : getPathNodeDisplayName(lastTask);
  } else if (lastTask) {
    endName = getPathNodeDisplayName(lastTask);
  } else if (lastEndEvent) {
    endName = getPathNodeDisplayName(lastEndEvent);
  }

  return {
    start: startName,
    end: endName,
  };
};

const getDecisionSummary = (path = []) =>
  path
    .filter((node) => {
      const kind = String(node?.kind || "").toLowerCase();
      return (
        kind.includes("exclusive") ||
        kind.includes("inclusive") ||
        kind.includes("parallel") ||
        kind.includes("gateway")
      );
    })
    .map((node) => ({
      name: getPathNodeName(node),
      condition: decodePathText(node?.condition || "selected"),
    }));

const getPathStats = (path = []) => {
  const subprocesses = path.filter((node) => isPathSubProcessNode(node)).length;
  const tasks = path.filter((node) => isTaskNode(node)).length;
  const decisions = getDecisionSummary(path).length;
  const parallel = path.filter((node) =>
    String(node?.kind || "").toLowerCase().includes("parallel"),
  ).length;
  return { steps: path.length, tasks, subprocesses, decisions, parallel };
};

const getRouteLabel = (path = [], index = 0) => {
  const firstNode = path[0];
  const kind = String(firstNode?.kind || "").toLowerCase();
  const isGatewayRoot =
    firstNode &&
    (kind.includes("inclusive") ||
      kind.includes("exclusive") ||
      kind.includes("parallel") ||
      kind.includes("gateway")) &&
    (Array.isArray(firstNode.activeBranches) ||
      Array.isArray(firstNode.branches) ||
      Array.isArray(firstNode.conditions));

  if (isGatewayRoot) {
    const gatewayName = getPathNodeName(firstNode);
    let conditions = [];
    if (Array.isArray(firstNode.activeBranches)) {
      conditions = firstNode.activeBranches
        .map((b) => decodePathText(b?.condition || ""))
        .filter(Boolean);
    } else if (Array.isArray(firstNode.conditions)) {
      conditions = firstNode.conditions
        .map((c) => decodePathText(c || ""))
        .filter(Boolean);
    } else if (Array.isArray(firstNode.branches)) {
      conditions = firstNode.branches
        .map((b) =>
          decodePathText((typeof b === "object" && b ? b.condition : "") || "")
        )
        .filter(Boolean);
    }
    const condStr = conditions.join(" + ");
    const { start, end } = getPathStartEnd(path);
    return {
      name: `Path ${index + 1}`,
      short: condStr ? `${gatewayName}: [${condStr}]` : `${start} → ${end}`,
      detail: `${start} → ${end}`,
    };
  }

  const { start, end } = getPathStartEnd(path);
  return {
    name: `Path ${index + 1}`,
    short: `${start} → ${end}`,
  };
};

const getPathNodeKey = (node) => {
  const kind = isPathSubProcessNode(node)
    ? "subprocess"
    : isTaskNode(node)
    ? "task"
    : String(node?.kind || "task").toLowerCase();
  return String(node?.key || `${kind}-${node?.id || getPathNodeName(node)}`);
};

const START_PATH_NODE = {
  kind: "start",
  id: "__path_start__",
  name: "Start",
};

const END_PATH_NODE = {
  kind: "end",
  id: "__path_end__",
  name: "End",
};

const getAllPathStats = (paths = []) => {
  const nodeMap = new Map();
  paths.forEach((path) => {
    path.forEach((node) => nodeMap.set(getPathNodeKey(node), node));
  });
  const nodes = Array.from(nodeMap.values());
  return getPathStats(nodes);
};

const PathRouteSelect = ({
  paths,
  selectedPathIndex,
  setSelectedPathIndex,
  dropdownOpen,
  setDropdownOpen,
}) => {
  const isAllSelected = selectedPathIndex === -1;
  const selectedPath = paths[selectedPathIndex] || [];
  const currentLabel =
    isAllSelected ?
      { name: "All Paths", short: "Complete BPMN view" }
    : getRouteLabel(selectedPath, selectedPathIndex);
  const currentStats =
    isAllSelected ? getAllPathStats(paths) : getPathStats(selectedPath);

  return (
    <div style={{ position: "relative", width: 320, maxWidth: "100%" }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#94a3b8",
          marginBottom: 6,
        }}
      >
        Select Path
      </div>
      <button
        type="button"
        onClick={() => setDropdownOpen((open) => !open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          background: "#fff",
          border: "1.5px solid #2563eb",
          borderRadius: 11,
          padding: "11px 14px",
          cursor: "pointer",
          boxShadow: "0 4px 14px rgba(37,99,235,0.12)",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              borderRadius: "50%",
              background: "#2563eb",
              color: "#fff",
              fontSize: 12,
              fontWeight: 800,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isAllSelected ? "A" : selectedPathIndex + 1}
          </span>
          <span style={{ minWidth: 0, textAlign: "left" }}>
            <span
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 800,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentLabel.name}
            </span>
            <span style={{ display: "block", color: "#64748b", fontSize: 11 }}>
              {isAllSelected
                ? `${currentStats.tasks} tasks · ${currentStats.decisions} decisions`
                : `${currentStats.tasks} tasks · ${currentStats.decisions} decisions`}
            </span>
            {!isAllSelected && currentLabel.detail && (
              <span style={{ display: "block", color: "#94a3b8", fontSize: 10, marginTop: 1 }}>
                {currentLabel.detail}
              </span>
            )}
          </span>
        </span>
        <span
          style={{
            color: "#2563eb",
            fontSize: 11,
            transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          ▼
        </span>
      </button>

      {dropdownOpen && (
        <div
          style={{
            position: "absolute",
            zIndex: 30,
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 8,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 12,
            boxShadow: "0 18px 44px rgba(16,24,40,0.18)",
            padding: 6,
            maxHeight: 340,
            overflow: "auto",
          }}
        >
          {[
            { path: null, pathIdx: -1, label: { short: "All Paths" }, stats: getAllPathStats(paths) },
            ...paths.map((path, pathIdx) => ({
              path,
              pathIdx,
              label: getRouteLabel(path, pathIdx),
              stats: getPathStats(path),
            })),
          ].map(({ pathIdx, label, stats }) => {
            const selected = pathIdx === selectedPathIndex;
            return (
              <button
                key={`path-option-${pathIdx}`}
                type="button"
                onClick={() => {
                  setSelectedPathIndex(pathIdx);
                  setDropdownOpen(false);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: selected ? "#eff4ff" : "transparent",
                  border: "none",
                  borderRadius: 9,
                  padding: "9px 11px",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    borderRadius: "50%",
                    background: selected ? "#2563eb" : "#e2e8f0",
                    color: selected ? "#fff" : "#475569",
                    fontSize: 12,
                    fontWeight: 800,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {pathIdx === -1 ? "A" : pathIdx + 1}
                </span>
                <span style={{ minWidth: 0, textAlign: "left", flex: 1 }}>
                  <span
                    style={{
                      display: "block",
                      fontSize: 12.5,
                      fontWeight: 800,
                      color: "#0f172a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {pathIdx === -1 ? "All Paths" : label.short}
                  </span>
                  <span style={{ display: "block", color: "#64748b", fontSize: 11 }}>
                    {stats.tasks} tasks · {stats.decisions} decisions
                  </span>
                  {label.detail && (
                    <span style={{ display: "block", color: "#94a3b8", fontSize: 10, marginTop: 1 }}>
                      {label.detail}
                    </span>
                  )}
                </span>
                {selected && (
                  <span style={{ flexShrink: 0, color: "#2563eb", fontWeight: 800 }}>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PathLegend = () => (
  <div
    style={{
      display: "flex",
      gap: 18,
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: 10,
    }}
  >
    {[
      ["Task", "#3b82f6", "rect"],
      ["Sub-Process", "#b45309", "subprocess"],
      ["Decision", "#dc2626", "diamond"],
      ["Parallel", "#16a34a", "diamond"],
      ["Selected path", "#2563eb", "line"],
    ].map(([label, color, shape]) => (
      <span
        key={label}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          color: "#64748b",
          fontSize: 11.5,
          fontWeight: 600,
        }}
      >
        <span
          style={{
            width: shape === "line" ? 18 : 16,
            height: shape === "line" ? 3 : 12,
            borderRadius: shape === "diamond" ? 2 : 3,
            border:
              shape === "line" ? "none"
              : shape === "subprocess" ? `2px double ${color}`
              : `1.5px solid ${color}`,
            borderLeft:
              shape === "rect" ? `4px solid ${color}`
              : shape === "subprocess" ? `4px solid ${color}`
              : undefined,
            background: shape === "line" ? color : shape === "subprocess" ? "#fffbeb" : "#fff",
            transform: shape === "diamond" ? "rotate(45deg)" : "none",
            display: "block",
          }}
        />
        {label}
      </span>
    ))}
  </div>
);

const PathDiagramNode = ({
  node,
  index,
  x,
  y,
  active = true,
  dimmed = false,
}) => {
  const kind = String(node?.kind || "task").toLowerCase();
  const isSubProcess = isPathSubProcessNode(node);
  const name = isSubProcess ? getPathNodeDisplayName(node) : getPathNodeName(node);
  const isDecision = kind.includes("exclusive") || kind.includes("inclusive");
  const isParallel = kind.includes("parallel");
  const isEndpoint = kind === "start" || kind === "end";
  const stateStyle = {
    opacity: dimmed ? 0.28 : 1,
    filter: dimmed ? "grayscale(1) blur(0.8px)" : "none",
    transition: "opacity 0.2s ease, filter 0.2s ease",
  };

  if (isEndpoint) {
    const color = kind === "start" ? "#16a34a" : "#475569";
    return (
      <div
        style={{
          position: "absolute",
          left: x - 30,
          top: y - 30,
          width: 60,
          height: 60,
          zIndex: 2,
          ...stateStyle,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: "#fff",
            border: `3px solid ${color}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            fontSize: 16,
            fontWeight: 900,
            boxShadow: active ? `0 0 0 5px ${kind === "start" ? "rgba(22,163,74,0.14)" : "rgba(71,85,105,0.14)"}` : "none",
          }}
        >
          {kind === "start" ? "▶" : "■"}
        </div>
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "100%",
            transform: "translate(-50%, 4px)",
            color: "#64748b",
            fontSize: 11,
            fontWeight: 800,
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </div>
        {/* Step number badge */}
        <span
          style={{
            position: "absolute",
            left: -8,
            top: -8,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: color,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 900,
            border: "2px solid #fff",
            zIndex: 3,
          }}
        >
          {index + 1}
        </span>
      </div>
    );
  }

  if (isDecision || isParallel) {
    const gatewaySize = 40;
    // Per-type BPMN symbol, color, and shadow
    const isExclusive = kind.includes("exclusive");
    const isInclusive = kind.includes("inclusive");
    // isParallel already defined above
    const gatewaySymbol = isExclusive ? "✕" : isInclusive ? "○" : "+";
    const gatewayColor = isExclusive ? "#dc2626"
      : isInclusive ? "#d97706"
      : "#16a34a";
    const gatewayBadgeBg = isExclusive ? "#dc2626"
      : isInclusive ? "#d97706"
      : "#16a34a";
    const gatewayShadow = isExclusive
      ? "rgba(220,38,38,0.24)"
      : isInclusive
      ? "rgba(217,119,6,0.24)"
      : "rgba(22,163,74,0.2)";
    const gatewayLabelColor = isExclusive ? "#b91c1c"
      : isInclusive ? "#92400e"
      : "#166534";
    return (
      <div
        style={{
          position: "absolute",
          left: x - gatewaySize / 2,
          top: y - gatewaySize / 2,
          width: gatewaySize,
          height: gatewaySize,
          zIndex: 2,
          ...stateStyle,
        }}
      >
        {/* Step number badge */}
        <span
          style={{
            position: "absolute",
            left: -8,
            top: -8,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: gatewayBadgeBg,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 900,
            border: "2px solid #fff",
            zIndex: 3,
          }}
        >
          {index + 1}
        </span>
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: "100%",
            transform: "translate(-50%, -6px)",
            whiteSpace: "nowrap",
            color: gatewayLabelColor,
            background: "#fafbfc",
            borderRadius: 5,
            padding: "2px 6px",
            fontSize: 10.5,
            fontWeight: 800,
          }}
        >
          {name}
        </div>
        <div
          style={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            border: `2px solid ${gatewayColor}`,
            transform: "rotate(45deg)",
            boxShadow: active ? `0 8px 18px ${gatewayShadow}` : "none",
          }}
        >
          <span
            style={{
              transform: "rotate(-45deg)",
              color: gatewayColor,
              fontSize: isInclusive ? 18 : 14,
              fontWeight: 900,
              lineHeight: 1,
            }}
          >
            {gatewaySymbol}
          </span>
        </div>
      </div>
    );
  }

  if (isSubProcess) {
    return (
      <div
        style={{
          position: "absolute",
          left: x - 92,
          top: y - 38,
          width: 184,
          height: 76,
          zIndex: 2,
          ...stateStyle,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 3,
            background: active ? "#fffbeb" : "#fff",
            border: `2px solid ${active ? "#b45309" : "#fcd34d"}`,
            outline: `2px solid ${active ? "#f59e0b" : "#fde68a"}`,
            outlineOffset: -5,
            borderLeft: `5px solid ${active ? "#b45309" : "#d97706"}`,
            borderRadius: 11,
            padding: "8px 12px",
            boxShadow: active
              ? "0 8px 20px rgba(180,83,9,0.22)"
              : "0 1px 3px rgba(16,24,40,0.06)",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: "#b45309",
              fontSize: 9.5,
              fontWeight: 900,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            <span aria-hidden="true">◇</span>
            Sub-Process
          </span>
          <span style={{ color: "#92400e", fontSize: 12, fontWeight: 800, lineHeight: 1.25 }}>
            {name}
          </span>
        </div>
        <span
          style={{
            position: "absolute",
            left: -10,
            top: -10,
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "#b45309",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 900,
            border: "2px solid #fff",
          }}
        >
          {index + 1}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        left: x - 84,
        top: y - 32,
        width: 168,
        height: 64,
        zIndex: 2,
        ...stateStyle,
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          background: "#fff",
          border: `1.5px solid ${active ? "#1d4ed8" : "#dbe3ee"}`,
          borderLeft: `5px solid ${active ? "#1d4ed8" : "#94a3b8"}`,
          borderRadius: 11,
          padding: "6px 12px",
          boxShadow: active ? "0 8px 20px rgba(37,99,235,0.2)" : "0 1px 3px rgba(16,24,40,0.06)",
        }}
      >
        <span style={{ color: "#1e293b", fontSize: 12.5, fontWeight: 800, lineHeight: 1.25 }}>
          {name}
        </span>
      </div>
      <span
        style={{
          position: "absolute",
          left: -10,
          top: -10,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#2563eb",
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 10,
          fontWeight: 900,
          border: "2px solid #fff",
        }}
      >
        {index + 1}
      </span>
    </div>
  );
};

/**
 * Builds a directed acyclic graph from the branch-preserving `graphPaths`.
 *
 * Unlike a naive linear chain, gateway nodes fan out: the gateway connects to
 * the first step of every branch, each branch is chained internally, and every
 * branch tail flows into whatever follows the gateway (its convergence point,
 * or End). Shared nodes (same kind + id) are de-duplicated by key so branches
 * that merge back onto a common step converge naturally.
 */
const buildPathGraph = (graphPaths = []) => {
  const nodeMap = new Map();
  const edges = new Map();
  const nodeConditionByPath = new Map();

  const startKey = getPathNodeKey(START_PATH_NODE);
  const endKey = getPathNodeKey(END_PATH_NODE);
  nodeMap.set(startKey, START_PATH_NODE);

  const registerNode = (node, pathIdx) => {
    const key = getPathNodeKey(node);
    if (!nodeMap.has(key)) nodeMap.set(key, node);
    const kind = String(node?.kind || "").toLowerCase();
    if (
      (kind.includes("exclusive") || kind.includes("inclusive")) &&
      String(node?.condition || "").trim()
    ) {
      if (!nodeConditionByPath.has(key)) nodeConditionByPath.set(key, {});
      nodeConditionByPath.get(key)[pathIdx] = decodePathText(node.condition);
    }
    return key;
  };

  const addEdge = (fromKey, toKey, pathIdx, label = "") => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const edgeKey = `${fromKey}>${toKey}`;
    const existing = edges.get(edgeKey);
    if (existing) {
      existing.pathIndexes.add(pathIdx);
      if (label && !existing.condition) existing.condition = label;
    } else {
      edges.set(edgeKey, {
        from: fromKey,
        to: toKey,
        pathIndexes: new Set([pathIdx]),
        condition: label || "",
      });
    }
  };

  const gatewayCondition = (node) => {
    const kind = String(node?.kind || "").toLowerCase();
    if (kind.includes("exclusive") || kind.includes("inclusive")) {
      return decodePathText(node?.condition || "").trim();
    }
    return "";
  };

  // Walks a structured sequence, wiring edges from `incomingTails` and returning
  // the set of tail keys that should connect to whatever comes next. `nextLabel`
  // is the condition printed on the very next edge (a branch condition for the
  // new format, or a gateway's own condition for the flat/old format).
  const walk = (nodes, incomingTails, pathIdx, incomingLabel = "") => {
    let tails = incomingTails;
    let nextLabel = incomingLabel;
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      if (!node || typeof node !== "object") return;
      const key = registerNode(node, pathIdx);
      tails.forEach((tail) => addEdge(tail, key, pathIdx, nextLabel));
      nextLabel = "";

      const branches = Array.isArray(node.branches) ? node.branches : [];
      if (branches.length > 0) {
        const branchTails = [];
        branches.forEach((branch) => {
          const branchNodes = Array.isArray(branch?.nodes)
            ? branch.nodes
            : Array.isArray(branch)
              ? branch
              : [];
          const branchLabel = decodePathText(branch?.condition || "").trim();
          const resolved = walk(branchNodes, [key], pathIdx, branchLabel);
          // Empty branch -> the gateway itself is the tail (connects to next),
          // and its condition rides the edge to whatever comes after.
          if (resolved.length) {
            branchTails.push(...resolved);
          } else {
            branchTails.push(key);
            if (branchLabel) nextLabel = branchLabel;
          }
        });
        tails = branchTails;
      } else {
        tails = [key];
        // Flat/old format: the gateway's own condition labels its outgoing edge.
        const ownCondition = gatewayCondition(node);
        if (ownCondition) nextLabel = ownCondition;
      }
    });
    return tails;
  };

  graphPaths.forEach((path, pathIdx) => {
    const tails = walk(path, [startKey], pathIdx);
    tails.forEach((tail) => addEdge(tail, endKey, pathIdx));
  });

  // Ensure End is present and ordered last.
  nodeMap.delete(endKey);
  nodeMap.set(endKey, END_PATH_NODE);

  return {
    nodes: Array.from(nodeMap.entries()).map(([key, node]) => ({
      key,
      node,
      conditionByPathIndex: nodeConditionByPath.get(key) || {},
    })),
    edges: Array.from(edges.values()),
  };
};

const getDiagramHalfWidth = (node) => {
  const kind = String(node?.kind || "task").toLowerCase();
  if (kind === "start" || kind === "end") return 32;
  if (kind.includes("exclusive") || kind.includes("inclusive") || kind.includes("parallel")) {
    return 36;
  }
  if (isPathSubProcessNode(node)) return 98;
  return 90;
};

// Layered-layout spacing.
const PATH_COL_GAP = 300; // horizontal distance between columns (ranks)
const PATH_LANE_GAP = 184; // vertical distance between branch lanes (rows)
const PATH_START_X = 128;
const PATH_TOP_PAD = 108;
const PATH_BOTTOM_PAD = 112;

/**
 * Sugiyama-style layered layout for the path DAG.
 *
 * 1. Rank (column / x) is the longest path from Start, so edges always flow
 *    left-to-right and a node sits after every one of its predecessors.
 * 2. Lane (row / y) is assigned per node in topological order: each node prefers
 *    the median lane of its predecessors and takes the nearest free lane in its
 *    column, which keeps straight chains aligned and fans gateway branches out
 *    into separate rows.
 */
const computeLayeredLayout = (graph) => {
  const nodes = graph.nodes;
  const keys = nodes.map((n) => n.key);
  const indexByKey = new Map(keys.map((k, i) => [k, i]));
  const outAdj = new Map(keys.map((k) => [k, []]));
  const inAdj = new Map(keys.map((k) => [k, []]));
  graph.edges.forEach((e) => {
    if (!outAdj.has(e.from) || !inAdj.has(e.to)) return;
    outAdj.get(e.from).push(e.to);
    inAdj.get(e.to).push(e.from);
  });

  // Longest-path ranking via Kahn topological order.
  const indeg = new Map(keys.map((k) => [k, (inAdj.get(k) || []).length]));
  const rank = new Map(keys.map((k) => [k, 0]));
  const queue = keys.filter((k) => (indeg.get(k) || 0) === 0);
  const topo = [];
  const working = new Map(indeg);
  while (queue.length) {
    const k = queue.shift();
    topo.push(k);
    (outAdj.get(k) || []).forEach((t) => {
      rank.set(t, Math.max(rank.get(t) || 0, (rank.get(k) || 0) + 1));
      working.set(t, (working.get(t) || 0) - 1);
      if (working.get(t) === 0) queue.push(t);
    });
  }
  // Any node not reached (defensive against cycles) keeps rank 0 but is still
  // laid out below.
  keys.forEach((k) => {
    if (!topo.includes(k)) topo.push(k);
  });

  const maxRank = keys.reduce((m, k) => Math.max(m, rank.get(k) || 0), 0);

  // Lane assignment — fork gateway branches into separate rows.
  //
  // A node whose (single) parent has more than one successor is a branch head:
  // it is offset symmetrically above/below the parent's lane by its branch
  // index, and that offset is carried down the branch by every subsequent
  // single-successor step. A convergence node (more than one predecessor) is
  // pulled back to the average of its predecessors, so branches visibly split
  // after a gateway and merge again at the join.
  const laneByKey = new Map();
  topo.forEach((k) => {
    const preds = inAdj.get(k) || [];
    if (preds.length === 0) {
      laneByKey.set(k, 0);
      return;
    }
    if (preds.length === 1) {
      const p = preds[0];
      const parentLane = laneByKey.get(p) ?? 0;
      const siblings = outAdj.get(p) || [];
      if (siblings.length > 1) {
        const branchIdx = Math.max(0, siblings.indexOf(k));
        laneByKey.set(k, parentLane + (branchIdx - (siblings.length - 1) / 2));
      } else {
        laneByKey.set(k, parentLane);
      }
      return;
    }
    const predLanes = preds.map((p) => laneByKey.get(p) ?? 0);
    laneByKey.set(
      k,
      predLanes.reduce((a, b) => a + b, 0) / predLanes.length,
    );
  });

  // De-collide nodes that landed on (nearly) the same lane within a column.
  const MIN_LANE_SEP = 0.9;
  const byRank = [];
  for (let r = 0; r <= maxRank; r += 1) byRank.push([]);
  keys.forEach((k) => byRank[rank.get(k) || 0].push(k));
  byRank.forEach((col) => {
    col.sort(
      (a, b) =>
        (laneByKey.get(a) ?? 0) - (laneByKey.get(b) ?? 0) ||
        indexByKey.get(a) - indexByKey.get(b),
    );
    for (let i = 1; i < col.length; i += 1) {
      const prev = laneByKey.get(col[i - 1]) ?? 0;
      const cur = laneByKey.get(col[i]) ?? 0;
      if (cur - prev < MIN_LANE_SEP) {
        laneByKey.set(col[i], prev + MIN_LANE_SEP);
      }
    }
  });

  let minLane = Infinity;
  let maxLane = -Infinity;
  laneByKey.forEach((lane) => {
    if (lane < minLane) minLane = lane;
    if (lane > maxLane) maxLane = lane;
  });
  if (!Number.isFinite(minLane)) {
    minLane = 0;
    maxLane = 0;
  }
  const laneCenter = (minLane + maxLane) / 2;
  const laneSpan = maxLane - minLane + 1;

  return {
    rankByKey: rank,
    laneByKey,
    laneCenter,
    maxRank,
    laneSpan,
    indexByKey,
  };
};

/**
 * Orthogonal connector between two node anchor points: a horizontal run out of
 * the source, a single vertical step at a shared trunk, then a horizontal run
 * into the target — with rounded corners. Horizontal segments therefore line up
 * exactly with node centers, so gateway fan-out / fan-in reads like a BPMN chart.
 * Endpoints on the same lane collapse to a straight horizontal line.
 */
const getConnectorGeometry = (from, to) => {
  const startX = from.x + getDiagramHalfWidth(from.node) + 6;
  const endX = to.x - getDiagramHalfWidth(to.node) - 6;
  const startY = from.y;
  const endY = to.y;

  if (Math.abs(startY - endY) < 0.5) {
    return { d: `M ${startX} ${startY} L ${endX} ${endY}`, startX, startY, endX, endY };
  }

  const down = endY > startY ? 1 : -1;
  // Trunk sits a little after the source so fan-out branches share one clean
  // spine while keeping vertical runs clear of the node boxes.
  const trunkX = startX + Math.min(52, Math.max(20, (endX - startX) * 0.42));
  const r = Math.min(
    16,
    Math.abs(endY - startY) / 2,
    Math.max(0, trunkX - startX),
    Math.max(0, endX - trunkX),
  );

  const d = [
    `M ${startX} ${startY}`,
    `L ${trunkX - r} ${startY}`,
    `Q ${trunkX} ${startY} ${trunkX} ${startY + down * r}`,
    `L ${trunkX} ${endY - down * r}`,
    `Q ${trunkX} ${endY} ${trunkX + r} ${endY}`,
    `L ${endX} ${endY}`,
  ].join(" ");
  return { d, startX, startY, endX, endY };
};

const PathDiagram = ({ graphPaths, paths, selectedPathIndex }) => {
  const graph = React.useMemo(
    () => buildPathGraph(graphPaths && graphPaths.length ? graphPaths : paths),
    [graphPaths, paths],
  );
  const layout = React.useMemo(() => computeLayeredLayout(graph), [graph]);

  const selectedKeys = React.useMemo(() => {
    if (selectedPathIndex === -1) return null;
    const selectedPath = paths[selectedPathIndex] || [];
    return new Set(
      [START_PATH_NODE, ...selectedPath, END_PATH_NODE].map((node) =>
        getPathNodeKey(node),
      ),
    );
  }, [paths, selectedPathIndex]);

  const centerY =
    PATH_TOP_PAD + ((layout.laneSpan - 1) / 2) * PATH_LANE_GAP;

  const positions = React.useMemo(
    () =>
      graph.nodes.map(({ key, node, conditionByPathIndex }, index) => {
        const r = layout.rankByKey.get(key) || 0;
        const lane = (layout.laneByKey.get(key) || 0) - layout.laneCenter;
        return {
          key,
          node,
          conditionByPathIndex,
          order: index,
          rank: r,
          x: PATH_START_X + r * PATH_COL_GAP,
          y: centerY + lane * PATH_LANE_GAP,
        };
      }),
    [graph.nodes, layout, centerY],
  );
  const positionByKey = React.useMemo(
    () => new Map(positions.map((pos) => [pos.key, pos])),
    [positions],
  );

  const canvasWidth = Math.max(
    980,
    PATH_START_X * 2 + Math.max(layout.maxRank, 1) * PATH_COL_GAP,
  );
  const canvasHeight = Math.max(
    360,
    PATH_TOP_PAD + PATH_BOTTOM_PAD + Math.max(layout.laneSpan - 1, 0) * PATH_LANE_GAP + 64,
  );

  return (
    <div
      style={{
        position: "relative",
        border: "1px solid #e6eaf0",
        borderRadius: 14,
        background: "#fafbfc",
        backgroundImage: "radial-gradient(circle, #e2e8f0 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        height: canvasHeight,
        overflow: "auto",
      }}
    >
      <div style={{ position: "relative", width: canvasWidth, height: canvasHeight - 20 }}>
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: canvasWidth,
            height: canvasHeight - 20,
            overflow: "visible",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <defs>
            <marker
              id="path-arrow-hot"
              markerUnits="userSpaceOnUse"
              markerWidth="11"
              markerHeight="11"
              refX="7.5"
              refY="3.2"
              orient="auto"
            >
              <path d="M0,0 L7.5,3.2 L0,6.4 Z" fill="#2563eb" />
            </marker>
            <marker
              id="path-arrow-dim"
              markerUnits="userSpaceOnUse"
              markerWidth="10"
              markerHeight="10"
              refX="7"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L7,3 L0,6 Z" fill="#94a3b8" />
            </marker>
          </defs>
          {graph.edges.map((edge, index) => {
            const from = positionByKey.get(edge.from);
            const to = positionByKey.get(edge.to);
            if (!from || !to) return null;
            const hot = selectedPathIndex === -1 || edge.pathIndexes.has(selectedPathIndex);
            const dim = selectedPathIndex !== -1 && !hot;
            const geometry = getConnectorGeometry(from, to);
            return (
              <g key={`edge-${index}`}>
                <path
                  d={geometry.d}
                  fill="none"
                  stroke={hot ? "#2563eb" : "#cbd5e1"}
                  strokeWidth={hot ? "3.25" : "2"}
                  strokeOpacity={dim ? 0.38 : 0.95}
                  strokeDasharray={hot ? "0" : "4 5"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  markerEnd={hot ? "url(#path-arrow-hot)" : "url(#path-arrow-dim)"}
                  style={{ transition: "stroke 0.2s ease, opacity 0.2s ease" }}
                />
                <circle
                  cx={geometry.startX}
                  cy={geometry.startY}
                  r={hot ? 2.2 : 1.7}
                  fill={hot ? "#2563eb" : "#cbd5e1"}
                />
                <circle
                  cx={geometry.endX}
                  cy={geometry.endY}
                  r={hot ? 2.2 : 1.7}
                  fill={hot ? "#2563eb" : "#cbd5e1"}
                />
                {edge.condition
                  ? (() => {
                      const straight =
                        Math.abs(geometry.startY - geometry.endY) < 0.5;
                      const trunkX = Math.min(
                        geometry.startX + 34,
                        (geometry.startX + geometry.endX) / 2,
                      );
                      const w = Math.max(22, edge.condition.length * 6.2 + 14);
                      const cx = straight
                        ? (geometry.startX + geometry.endX) / 2
                        : trunkX + 10 + w / 2;
                      const cy = straight
                        ? geometry.startY - 12
                        : (geometry.startY + geometry.endY) / 2;
                      return (
                        <g opacity={dim ? 0.45 : 1}>
                          <rect
                            x={cx - w / 2}
                            y={cy - 9}
                            width={w}
                            height={18}
                            rx={9}
                            fill={hot ? "#eff6ff" : "#f8fafc"}
                            stroke={hot ? "#bfdbfe" : "#e2e8f0"}
                          />
                          <text
                            x={cx}
                            y={cy + 3.5}
                            textAnchor="middle"
                            fontSize="10.5"
                            fontWeight="700"
                            fill={hot ? "#1d4ed8" : "#94a3b8"}
                          >
                            {edge.condition}
                          </text>
                        </g>
                      );
                    })()
                  : null}
              </g>
            );
          })}
        </svg>
        {positions.map((pos, index) => (
          <PathDiagramNode
            key={`${pos.node?.id || index}-${index}`}
            node={pos.node}
            index={index}
            x={pos.x}
            y={pos.y}
            active={selectedPathIndex === -1 || selectedKeys?.has(pos.key)}
            dimmed={selectedPathIndex !== -1 && !selectedKeys?.has(pos.key)}
          />
        ))}
      </div>
    </div>
  );
};

const ProcessPathsTab = ({ processId, processName, isActive }) => {
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [pathData, setPathData] = React.useState(() => normalizePathPayload(null));
  const [error, setError] = React.useState("");
  const [selectedPathIndex, setSelectedPathIndex] = React.useState(-1);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const loadPaths = React.useCallback(async () => {
    if (!processId) return;
    try {
      setLoading(true);
      setError("");
      const payload = await getProcessPaths(processId);
      const normalized = normalizePathPayload(payload);
      setPathData(normalized);
      setSelectedPathIndex(-1);
      setLoaded(true);
    } catch (e) {
      setPathData(normalizePathPayload(null));
      setSelectedPathIndex(-1);
      setError(e?.message || "Failed to load BPMN paths");
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [processId]);

  React.useEffect(() => {
    if (isActive && processId && !loaded && !loading) {
      loadPaths();
    }
  }, [isActive, loaded, loading, loadPaths, processId]);

  React.useEffect(() => {
    setLoaded(false);
    setPathData(normalizePathPayload(null));
    setSelectedPathIndex(-1);
    setDropdownOpen(false);
    setError("");
  }, [processId]);

  const paths = pathData.paths || [];
  const selectedPath = paths[selectedPathIndex] || paths[0] || [];
  const selectedStats =
    selectedPathIndex === -1 ? getAllPathStats(paths) : getPathStats(selectedPath);
  const selectedLabel =
    selectedPathIndex === -1 ?
      { short: "Complete BPMN view" }
    : getRouteLabel(selectedPath, selectedPathIndex);

  return (
    <div
      className="role-card"
      style={{
        border: "1px solid #e4e7ec",
        borderRadius: 16,
        padding: 0,
        overflow: "hidden",
        background: "#ffffff",
        boxShadow: "0 12px 40px rgba(16,24,40,0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          padding: "18px 24px",
          borderBottom: "1px solid #eef1f4",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
            <span style={{ width: 6, height: 26, borderRadius: 3, background: "#3b82f6" }} />
            <span style={{ width: 6, height: 26, borderRadius: 3, background: "#14b8a6" }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#0f172a", fontSize: 15, fontWeight: 800 }}>
              BPMN Path View
            </div>
            <div
              style={{
                color: "#64748b",
                fontSize: 12.5,
                fontWeight: 600,
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {processName || "Selected Process"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div
            style={{
              textAlign: "center",
              border: "1px solid #e2e8f0",
              borderRadius: 9,
              padding: "5px 14px",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                color: "#94a3b8",
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              Routes
            </div>
            <div style={{ color: "#2563eb", fontSize: 17, fontWeight: 900 }}>
              {pathData.totalPaths || paths.length || 0}
            </div>
          </div>
          <button
            type="button"
            className="secondary-btn"
            onClick={loadPaths}
            disabled={!processId || loading}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      <div style={{ padding: 20 }}>
        {loading && !paths.length ? (
          <div className="no-results">Loading BPMN paths...</div>
        ) : error ? (
          <div className="no-results">
            <p>{error}</p>
          </div>
        ) : paths.length === 0 ? (
          <div className="no-results">No BPMN paths found for this process.</div>
        ) : (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 18,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              <PathRouteSelect
                paths={paths}
                selectedPathIndex={selectedPathIndex}
                setSelectedPathIndex={setSelectedPathIndex}
                dropdownOpen={dropdownOpen}
                setDropdownOpen={setDropdownOpen}
              />

              <div style={{ flex: 1, minWidth: 220 }}>
                <div
                  style={{
                    color: "#2563eb",
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 7,
                  }}
                >
                  {selectedLabel.short}
                </div>
                <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
                  {[
                    [`${selectedStats.steps} total steps`, "#1e293b", "#eff4ff", "#d4e2ff"],
                    [`${selectedStats.tasks} work tasks`, "#1d4ed8", "#eff4ff", "#d4e2ff"],
                    [
                      `${selectedStats.subprocesses || 0} sub-processes`,
                      "#b45309",
                      "#fffbeb",
                      "#fcd34d",
                    ],
                    [`${selectedStats.decisions} decisions`, "#b91c1c", "#fef2f2", "#fbd5d5"],
                    [
                      selectedPathIndex === -1 ?
                        `All ${paths.length} paths`
                      : `${selectedPathIndex + 1} / ${paths.length}`,
                      "#475569",
                      "#f1f5f9",
                      "#e2e8f0",
                    ],
                  ].map(([label, color, bg, border]) => (
                    <span
                      key={label}
                      style={{
                        color,
                        background: bg,
                        border: `1px solid ${border}`,
                        borderRadius: 999,
                        padding: "6px 13px",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <PathLegend />
            <PathDiagram
              graphPaths={pathData.graphPaths}
              paths={paths}
              selectedPathIndex={selectedPathIndex}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const resolveChildProcessName = (pt, companyProcesses = []) => {
  const nested =
    pt?.child_process?.process_name ||
    pt?.child_process?.name ||
    pt?.child_process?.processName ||
    pt?.childProcess?.process_name ||
    pt?.childProcess?.name ||
    null;
  if (nested) return nested;
  const flat =
    pt?.child_process_name ||
    pt?.childProcessName ||
    pt?.process_name ||
    pt?.processName ||
    null;
  if (flat) return flat;
  const id = pt?.child_process_id ?? pt?.childProcessId;
  if (id == null || id === "") return null;
  const match = (companyProcesses || []).find(
    (p) => String(p.process_id || p.id) === String(id),
  );
  return match?.process_name || match?.name || null;
};

const RACI_FLAT_ROLES = [
  { role: "R", label: "Responsible" },
  { role: "A", label: "Accountable" },
  { role: "C", label: "Consulted" },
  { role: "I", label: "Informed" },
];

const SubprocessBadge = () => (
  <span
    className="raci-sub-badge"
    title="Sub-process — a child process embedded as a composite step. Its RACI is defined inside its own process."
  >
    Sub-Process
  </span>
);

const SubprocessRaciDetailRow = ({ colSpan, childProcessId, entry, onRetry }) => (
  <tr>
    <td colSpan={colSpan} className="raci-sub-detail-cell">
      <div className="raci-sub-detail-inner">
        {!entry || entry.loading ? (
          <div className="raci-sub-detail-loading">
            Loading the sub-process&apos;s RACI…
          </div>
        ) : entry.error ? (
          <div className="raci-sub-detail-error">
            {entry.error}{" "}
            <button
              type="button"
              className="secondary-btn sm"
              onClick={() => onRetry(childProcessId)}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="raci-sub-detail-title">
              {entry.processName || `Process #${childProcessId}`} — its own RACI (
              {entry.taskGroups.length} step
              {entry.taskGroups.length === 1 ? "" : "s"})
            </div>
            {entry.taskGroups.length === 0 ? (
              <div className="raci-sub-detail-empty">
                The sub-process has no workflow steps yet.
              </div>
            ) : (
              <div className="raci-flat-wrap raci-sub-detail-tablewrap">
                <table className="raci-flat-table">
                  <thead>
                    <tr>
                      <th className="raci-flat-th-task">Task Name</th>
                      <th className="raci-flat-th-job">Job Name</th>
                      <th className="raci-flat-th-code">Job Code</th>
                      {RACI_FLAT_ROLES.map(({ role, label }) => (
                        <th key={role} className="raci-flat-th-role">
                          <div className="raci-flat-role-header">
                            <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>
                              {role}
                            </span>
                            <span className="raci-flat-role-label">{label}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entry.taskGroups.flatMap((tg, tgIdx) => {
                      const isAlt = tgIdx % 2 !== 0;
                      if (tg.isChildProcess) {
                        return [
                          <tr
                            key={tg.task_id}
                            className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""} raci-flat-row-first`}
                          >
                            <td className="raci-flat-task-cell">
                              <div className="raci-flat-task-inner">
                                <span className="raci-flat-step">{tg.order}</span>
                                <div className="raci-flat-task-text">
                                  <span className="raci-flat-task-name">{tg.task_name}</span>
                                  {tg.task_code && (
                                    <span className="raci-flat-task-code">[{tg.task_code}]</span>
                                  )}
                                  <div className="raci-sub-pills">
                                    <SubprocessBadge />
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td colSpan={6} className="raci-flat-no-jobs">
                              Nested sub-process — open that process to see its RACI.
                            </td>
                          </tr>,
                        ];
                      }
                      const rows = tg.jobs.length > 0 ? tg.jobs : [null];
                      return rows.map((job, jIdx) => (
                        <tr
                          key={job ? `${tg.task_id}-${job.job_id}` : `empty-${tg.task_id}`}
                          className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""}${jIdx === 0 ? " raci-flat-row-first" : ""}`}
                        >
                          {jIdx === 0 && (
                            <td rowSpan={rows.length} className="raci-flat-task-cell">
                              <div className="raci-flat-task-inner">
                                <span className="raci-flat-step">{tg.order}</span>
                                <div className="raci-flat-task-text">
                                  <span className="raci-flat-task-name">{tg.task_name}</span>
                                  {tg.task_code && (
                                    <span className="raci-flat-task-code">[{tg.task_code}]</span>
                                  )}
                                </div>
                              </div>
                            </td>
                          )}
                          {job ? (
                            <>
                              <td className="raci-flat-job-name">{job.name}</td>
                              <td className="raci-flat-job-code">{job.code || "—"}</td>
                              {RACI_FLAT_ROLES.map(({ role }) => (
                                <td key={role} className="raci-flat-role-cell">
                                  {job.role === role ? (
                                    <span className={`raci-badge raci-${role.toLowerCase()}`}>
                                      {role}
                                    </span>
                                  ) : (
                                    <span className="raci-summary-empty">—</span>
                                  )}
                                </td>
                              ))}
                            </>
                          ) : (
                            <td colSpan={6} className="raci-flat-no-jobs">
                              No jobs assigned to this task
                            </td>
                          )}
                        </tr>
                      ));
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </td>
  </tr>
);

const ProcessViewPage = () => {
  const { setActiveSection } = useAppContext();
  const [loading, setLoading] = React.useState(true);
  const [proc, setProc] = React.useState(null);
  const [processId, setProcessId] = React.useState(null);
  const [companyProcesses, setCompanyProcesses] = React.useState([]);
  const [processKPIs, setProcessKPIs] = React.useState([]);
  const [kpiLoading, setKpiLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("basic");
  const { processId: processIdParam } = useParams();
  const location = useLocation();

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let id = null;
        if (processIdParam) {
          const parsed = Number(processIdParam);
          if (Number.isFinite(parsed) && parsed > 0) {
            id = parsed;
            try {
              localStorage.setItem("activeProcessId", String(parsed));
            } catch {}
          }
        }
        if (!Number.isFinite(id) || id <= 0) {
          const pathParts = String(location.pathname || "")
            .split("/")
            .filter(Boolean);
          const procIndex = pathParts.lastIndexOf("processes");
          if (procIndex >= 0) {
            const parsed = Number(pathParts[procIndex + 1]);
            if (Number.isFinite(parsed) && parsed > 0) {
              id = parsed;
              try {
                localStorage.setItem("activeProcessId", String(parsed));
              } catch {}
            }
          }
        }
        if (!Number.isFinite(id) || id <= 0) {
          try {
            const stored = Number(localStorage.getItem("activeProcessId"));
            if (Number.isFinite(stored) && stored > 0) {
              id = stored;
            }
          } catch {}
        }
        if (!Number.isFinite(id) || id <= 0) {
          setProcessId(null);
          setProc(null);
          setCompanyProcesses([]);
          toast.error("No process selected");
          return;
        }
        setProcessId(id);
        const processData = await getProcessWithRelations(id);
        setProc(processData || null);

        // Resolve child-process names the same way Edit Process does (company catalog).
        const companyId =
          processData?.company_id ||
          processData?.company?.company_id ||
          processData?.companyId ||
          "";
        if (companyId) {
          const companyProcessList = await getProcessesByCompanyId(companyId).catch(
            () => [],
          );
          setCompanyProcesses(
            Array.isArray(companyProcessList) ? companyProcessList : [],
          );
        } else {
          setCompanyProcesses([]);
        }

        // Load KPIs
        setKpiLoading(true);
        try {
          const kpisData = await getKPIsByProcessId(id);
          setProcessKPIs(Array.isArray(kpisData) ? kpisData : []);
        } catch (e) {
          console.error("Failed to load KPIs:", e);
          setProcessKPIs([]);
        } finally {
          setKpiLoading(false);
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load process");
      } finally {
        setLoading(false);
      }
    })();
  }, [processIdParam, location.pathname]);

  const onBack = () => setActiveSection("process-management");

  const processStatusLabel = React.useMemo(() => {
    return (
      proc?.process_status?.description ||
      proc?.status?.description ||
      proc?.processStatus?.description ||
      (proc?.process_status_id ? `Status ${proc.process_status_id}` : "-")
    );
  }, [proc]);

  const processCategoryLabel = React.useMemo(() => {
    return (
      proc?.process_category?.description ||
      proc?.category?.description ||
      proc?.processCategory?.description ||
      (proc?.process_category_id ? `Category ${proc.process_category_id}` : "-")
    );
  }, [proc]);

  const workflowRows = React.useMemo(() => {
    const rows =
      Array.isArray(proc?.process_tasks) ? [...proc.process_tasks] : [];
    rows.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    return rows.map((pt) => {
      if (pt.child_process_id || pt.childProcessId) {
        const childProcessId = pt.child_process_id ?? pt.childProcessId;
        const catalog = companyProcesses.find(
          (p) => String(p.process_id || p.id) === String(childProcessId),
        );
        return {
          order: pt.order,
          isChildProcess: true,
          childProcessId,
          childProcessName: resolveChildProcessName(pt, companyProcesses),
          childProcessCode:
            catalog?.process_code ||
            catalog?.code ||
            pt.child_process?.process_code ||
            pt.child_process?.code ||
            null,
          task: null,
          jobs: [],
        };
      }
      const t = pt.task || {};
      const jobs = mapJobTasksToWorkflowJobs(t.jobTasks);
      return { order: pt.order, task: t, jobs };
    });
  }, [proc, companyProcesses]);

  const raciRollup = React.useMemo(() => {
    const taskGroups = workflowRows
      .filter(
        (row) =>
          (row.isChildProcess && row.childProcessId != null) ||
          row.task?.task_id != null,
      )
      .map((row, idx) => {
        if (row.isChildProcess) {
          return {
            task_id: `sub-${row.childProcessId}`,
            isChildProcess: true,
            childProcessId: row.childProcessId,
            task_name: row.childProcessName || `Process #${row.childProcessId}`,
            task_code: row.childProcessCode || "",
            order: row.order ?? idx + 1,
            jobs: [],
          };
        }
        const jobTasks = Array.isArray(row.task?.jobTasks) ? row.task.jobTasks : [];
        const jobs = jobTasks
          .filter((jt) => jt?.job)
          .map((jt) => ({
            job_id: jt.job.job_id,
            name: jt.job.name || "-",
            code: jt.job.jobCode || jt.job.job_code || jt.job.code || "",
            role: jt.role || null,
          }));
        return {
          task_id: String(row.task.task_id),
          task_name: row.task.task_name || "-",
          task_code: row.task.task_code || "",
          order: row.order ?? idx + 1,
          jobs,
        };
      });
    const hasAssignments = taskGroups.some(
      (tg) => tg.jobs.length > 0 || tg.isChildProcess,
    );
    return { taskGroups, hasAssignments };
  }, [workflowRows]);

  const [expandedSubRaci, setExpandedSubRaci] = React.useState({});
  const [subRaciByProcess, setSubRaciByProcess] = React.useState({});

  React.useEffect(() => {
    setExpandedSubRaci({});
    setSubRaciByProcess({});
  }, [processId]);

  const loadSubRaci = React.useCallback(async (childProcessId) => {
    setSubRaciByProcess((prev) => ({
      ...prev,
      [childProcessId]: { loading: true },
    }));
    try {
      const data = await getProcessWithRelations(childProcessId);
      const pts = Array.isArray(data?.process_tasks) ? [...data.process_tasks] : [];
      pts.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
      const taskGroups = pts.map((pt, idx) => {
        const nestedChildId = pt.child_process_id ?? pt.childProcessId;
        if (nestedChildId != null) {
          return {
            task_id: `sub-${nestedChildId}`,
            isChildProcess: true,
            childProcessId: nestedChildId,
            task_name:
              resolveChildProcessName(pt, []) || `Process #${nestedChildId}`,
            task_code:
              pt.child_process?.process_code || pt.child_process?.code || "",
            order: pt.order ?? idx + 1,
            jobs: [],
          };
        }
        const t = pt.task || {};
        const jobTasks = Array.isArray(t.jobTasks) ? t.jobTasks : [];
        const jobs = jobTasks
          .filter((jt) => jt?.job)
          .map((jt) => ({
            job_id: jt.job.job_id,
            name: jt.job.name || "-",
            code: jt.job.jobCode || jt.job.job_code || jt.job.code || "",
            role: jt.role || null,
          }));
        return {
          task_id: String(t.task_id ?? `t-${idx}`),
          task_name: t.task_name || "-",
          task_code: t.task_code || "",
          order: pt.order ?? idx + 1,
          jobs,
        };
      });
      setSubRaciByProcess((prev) => ({
        ...prev,
        [childProcessId]: {
          loading: false,
          processName: data?.process_name || data?.name || "",
          taskGroups,
        },
      }));
    } catch (e) {
      setSubRaciByProcess((prev) => ({
        ...prev,
        [childProcessId]: {
          loading: false,
          error: e?.message || "Failed to load the sub-process's RACI.",
        },
      }));
    }
  }, []);

  const subRaciRequestedRef = React.useRef(new Set());
  React.useEffect(() => {
    subRaciRequestedRef.current = new Set();
  }, [processId]);

  const toggleSubRaci = React.useCallback(
    (childProcessId) => {
      setExpandedSubRaci((p) => ({ ...p, [childProcessId]: !p[childProcessId] }));
      if (!subRaciRequestedRef.current.has(childProcessId)) {
        subRaciRequestedRef.current.add(childProcessId);
        loadSubRaci(childProcessId);
      }
    },
    [loadSubRaci],
  );

  const retrySubRaci = React.useCallback(
    (childProcessId) => loadSubRaci(childProcessId),
    [loadSubRaci],
  );

  // Labels for gateway after/target resolution — tasks AND child processes
  // (same convention as Edit Process workflowTaskOptions).
  const taskLabelById = React.useMemo(() => {
    const map = new Map();
    workflowRows.forEach((row, idx) => {
      if (row.isChildProcess) {
        const id = row.childProcessId;
        if (!id) return;
        const name = row.childProcessName || `Process #${id}`;
        const code = row.childProcessCode;
        map.set(
          String(id),
          `${idx + 1}. ${name}${code ? ` [${code}]` : ""} (Subprocess)`,
        );
        return;
      }
      const id = row.task?.task_id;
      if (!id) return;
      const code = row.task.task_code;
      map.set(
        String(id),
        `${idx + 1}. ${row.task.task_name || "Task"}${code ? ` [${code}]` : ""}`,
      );
    });
    return map;
  }, [workflowRows]);

  const getKPIDisplayName = (kpi) => {
    if (kpi.kpiType === "TYPE2") {
      return kpi.custom_kpi_name || "Unnamed Custom KPI";
    }
    return kpi.kpi?.kpiName || "Unknown KPI";
  };

  const getKPIUoM = (kpi) => {
    if (kpi.kpiType === "TYPE2") {
      return kpi.custom_uom || "";
    }
    return kpi.kpi?.kpi_uom?.uomValue || "";
  };

  const gatewayRows = React.useMemo(() => {
    if (!Array.isArray(proc?.gateways)) return [];

    const firstVal = (...vals) => {
      for (const v of vals) {
        if (v != null && String(v) !== "") return String(v);
      }
      return "";
    };

    // Match Edit Process: named End / empty-target End stay as branches;
    // only "end after a specific task" (endTaskId, no name, no target) is an endpoint.
    const isEndpoint = (b) => {
      const target = firstVal(
        b.targetTaskId,
        b.target_task_id,
        b.target_gateway_id,
        b.targetGatewayId,
      );
      const endName = (b.endEventName || b.end_event_name || "").trim();
      const endTask = firstVal(b.endTaskId, b.end_task_id);
      return target === "" && endTask !== "" && endName === "";
    };

    return proc.gateways.map((g, gwIdx) => {
      const afterId =
        g.after_task_id ?? g.afterTaskId ?? g.after_task ?? g.afterTask;
      const allBranches = Array.isArray(g.branches) ? g.branches : [];

      const branches = allBranches
        .filter((b) => !isEndpoint(b))
        .map((b, idx) => {
          const gwTargetId = b.target_gateway_id ?? b.targetGatewayId ?? null;
          const gwTarget =
            gwTargetId != null
              ? (proc.gateways || []).find(
                  (x) => String(x.gateway_pk_id ?? x.id) === String(gwTargetId),
                )
              : null;
          const taskTarget = firstVal(b.targetTaskId, b.target_task_id, b.target_task);
          return {
            id: b.id ?? b.branch_pk_id ?? `${gwIdx}-b-${idx}`,
            targetTaskId:
              gwTargetId != null
                ? `__GW__${gwTargetId}`
                : taskTarget,
            targetGatewayName: gwTarget ? gwTarget.name || "Gateway" : "",
            endEventName: b.endEventName || b.end_event_name || "",
            condition: b.condition || "",
            isDefault: !!(b.is_default ?? b.isDefault),
            connectToEnd: (b.connectToEnd ?? b.connect_to_end) !== false,
          };
        });

      const endpoints = allBranches
        .filter(isEndpoint)
        .map((e, idx) => ({
          id: e.id ?? e.branch_pk_id ?? `${gwIdx}-e-${idx}`,
          endTaskId: firstVal(e.endTaskId, e.end_task_id),
          endEventName: e.end_event_name ?? e.endEventName ?? "",
          condition: e.condition || "",
        }));

      const convergeGwId = g.converge_at_gateway_id ?? g.convergeAtGatewayId;
      const afterGwId = g.after_gateway_id ?? g.afterGatewayId;
      const convergeTaskId = g.converge_at_task_id ?? g.convergeAtTaskId;

      return {
        id: g.gateway_pk_id ?? g.id ?? gwIdx,
        name: g.name || "",
        type: (g.gateway_type || g.type || "exclusive").toLowerCase(),
        afterTaskId: afterId != null && afterId !== "" ? String(afterId) : "",
        afterGatewayId:
          afterGwId != null && afterGwId !== "" ? String(afterGwId) : "",
        convergeAtTaskId:
          convergeTaskId != null && convergeTaskId !== ""
            ? String(convergeTaskId)
            : "",
        convergeGatewayName:
          g.convergeGatewayName || g.converge_gateway_name || "",
        convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
        convergeAtGatewayId:
          convergeGwId != null && convergeGwId !== ""
            ? String(convergeGwId)
            : "",
        branches,
        endpoints,
      };
    });
  }, [proc]);

  // gateway id → display name, for resolving a direct after-gateway placement.
  const gatewayNameById = React.useMemo(() => {
    const map = new Map();
    gatewayRows.forEach((g, idx) =>
      map.set(String(g.id), g.name || `Gateway ${idx + 1}`),
    );
    return map;
  }, [gatewayRows]);

  // A gateway placed after another gateway (join → split): gateway S's join flows
  // into gateway T's split, so T sits "after gateway S". Map each such target
  // gateway id → the display name of the gateway it follows, so the placement
  // field can show the gateway name just like a task or the Start Event.
  const afterGatewayByTargetId = React.useMemo(() => {
    const map = new Map();
    gatewayRows.forEach((g, idx) => {
      if (!g.convergeAtGatewayId) return;
      map.set(String(g.convergeAtGatewayId), g.name || `Gateway ${idx + 1}`);
    });
    return map;
  }, [gatewayRows]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">View Process</h2>
            <p className="page-subtitle">
              {proc?.process_name ?
                `Viewing: ${proc.process_name}`
              : "All fields are read-only"}
              {proc?.process_code ? ` (${proc.process_code})` : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={onBack}>
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : !proc ?
          <div className="no-results">No process data</div>
        : <SideTabs
            defaultActiveId="basic"
            activeId={activeTab}
            onChange={setActiveTab}
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Process Name</label>
                        <input value={proc.process_name || ""} disabled />
                      </div>
                      <div className="form-group">
                        <label>Process Code</label>
                        <input value={proc.process_code || ""} disabled />
                      </div>
                      <div className="form-group">
                        <label>Capacity Requirement (minutes)</label>
                        <input
                          value={proc.capacity_requirement_minutes ?? ""}
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Company</label>
                        <input
                          value={proc.company?.name || proc.company_name || "-"}
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Process Status</label>
                        <input value={processStatusLabel} disabled />
                      </div>
                      <div className="form-group">
                        <label>Process Category</label>
                        <input value={processCategoryLabel} disabled />
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <div className="form-group full">
                      <RichTextEditor
                        label="Process Overview"
                        value={proc.process_overview || ""}
                        readOnly
                        height={220}
                      />
                    </div>
                  </div>
                ),
              },
              {
                id: "workflow",
                label: "Workflow",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 12, fontWeight: 600 }}
                    >
                      Tasks in Workflow{" "}
                      {workflowRows?.length ? `(${workflowRows.length})` : ""}
                    </div>
                    <RaciLegendBar />
                    <div className="roles-table">
                      <div
                        className="roles-table-header"
                        style={{ gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 2.2fr) 0.6fr" }}
                      >
                        <div className="cell">Task</div>
                        <div className="cell">Jobs &amp; RACI</div>
                        <div className="cell">Order</div>
                      </div>
                      {workflowRows.length === 0 ?
                        <div className="no-results">
                          No tasks in this workflow
                        </div>
                      : workflowRows.map((row, idx) => {
                          if (row.isChildProcess) {
                            return (
                              <div
                                key={`child-${row.childProcessId || idx}`}
                                className="roles-table-row"
                                style={{ gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 2.2fr) 0.6fr" }}
                              >
                                <div className="cell">
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{
                                      minWidth: 24, height: 24, borderRadius: 6,
                                      background: "#b45309", color: "#fff",
                                      display: "inline-flex", alignItems: "center",
                                      justifyContent: "center", fontSize: 11,
                                      fontWeight: 700, flexShrink: 0, padding: "0 4px",
                                    }}>
                                      {idx + 1}
                                    </span>
                                    <span style={{
                                      width: 28, height: 28, borderRadius: 6,
                                      background: "#fef3c7", border: "1px solid #fcd34d",
                                      color: "#b45309", display: "inline-flex",
                                      alignItems: "center", justifyContent: "center",
                                      flexShrink: 0, fontSize: 14,
                                    }}>◇</span>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                      <span style={{ fontWeight: 600, color: "#92400e" }}>
                                        {row.childProcessName
                                          ? `${row.childProcessName}${
                                              row.childProcessCode
                                                ? ` [${row.childProcessCode}]`
                                                : ""
                                            }`
                                          : `Process #${row.childProcessId}`}
                                      </span>
                                      <span style={{ color: "#b45309", fontSize: 12 }}>Child Process</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="cell">
                                  <span style={{
                                    display: "inline-block", padding: "4px 8px",
                                    background: "#fef3c7", color: "#92400e",
                                    borderRadius: 9999, fontSize: 12,
                                    border: "1px solid #fcd34d",
                                  }}>
                                    Child Process
                                  </span>
                                </div>
                                <div className="cell">
                                  <span style={{
                                    display: "inline-block", minWidth: 28, textAlign: "center",
                                    padding: "4px 8px", background: "#f3f4f6",
                                    borderRadius: 8, border: "1px solid #e5e7eb",
                                  }}>
                                    {row.order ?? "-"}
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          const jobChips = (
                            <WorkflowJobChips jobs={row.jobs || []} />
                          );
                          return (
                            <div
                              key={`${row.task.task_id || idx}`}
                              className="roles-table-row"
                              style={{
                                gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 2.2fr) 0.6fr",
                              }}
                            >
                              <div className="cell">
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{
                                    minWidth: 24,
                                    height: 24,
                                    borderRadius: 6,
                                    background: "#2563eb",
                                    color: "#fff",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                    padding: "0 4px",
                                  }}>
                                    {idx + 1}
                                  </span>
                                  <span style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 6,
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                    color: "#2563eb",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}>
                                    <TaskIcon />
                                  </span>
                                  <div style={{ display: "flex", flexDirection: "column" }}>
                                    <span style={{ fontWeight: 600, color: "#111827" }}>
                                      {row.task.task_name || "-"}
                                    </span>
                                    {row.task.task_code && (
                                      <span style={{ color: "#6b7280", fontSize: 12 }}>
                                        [{row.task.task_code}]
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="cell">
                                {row.jobs?.length ? jobChips : (
                                  <span style={{ color: "#6b7280" }}>-</span>
                                )}
                              </div>
                              <div className="cell">
                                <span style={{
                                  display: "inline-block",
                                  minWidth: 28,
                                  textAlign: "center",
                                  padding: "4px 8px",
                                  background: "#f3f4f6",
                                  borderRadius: 8,
                                  border: "1px solid #e5e7eb",
                                }}>
                                  {row.order ?? "-"}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>

                    <div
                      className="role-card"
                      style={{
                        marginTop: 16,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        className="section-title"
                        style={{ marginBottom: 12, fontWeight: 600 }}
                      >
                         <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                           {/* Gateway section icon — matches gateway diamond shape in BPMN diagrams */}
                           <span style={{
                             width: 28,
                             height: 28,
                             background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                             borderRadius: 6,
                             display: "inline-flex",
                             alignItems: "center",
                             justifyContent: "center",
                             color: "white",
                             fontSize: 14,
                             fontWeight: 700,
                           }}>◇</span>
                           Gateways
                         </span>
                      </div>
                      {gatewayRows.length === 0 ?
                        <div className="no-results">
                          No gateways defined for this process
                        </div>
                      : <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                          }}
                        >
                          {gatewayRows.map((gw, gwIdx) => {
                            // "After" placement: Start Event, task/subprocess, or
                            // another gateway — same resolution as Edit Process.
                            const afterGatewayName =
                              (gw.afterGatewayId &&
                                gatewayNameById.get(String(gw.afterGatewayId))) ||
                              afterGatewayByTargetId.get(String(gw.id));
                            const afterLabel =
                              gw.afterTaskId &&
                              gw.afterTaskId !== "__START_EVENT__"
                                ? taskLabelById.get(String(gw.afterTaskId)) ||
                                  afterGatewayName ||
                                  "-"
                                : afterGatewayName || "Start Event";

                            let convergeLabel = null;
                            if (gw.convergeToEnd) {
                              convergeLabel = "⏹ End Event — synchronize, then end";
                            } else if (gw.convergeAtGatewayId) {
                              const gwName =
                                gatewayNameById.get(String(gw.convergeAtGatewayId)) ||
                                "Gateway";
                              convergeLabel = `▷ Gateway: ${gwName} — join into its split`;
                            } else if (gw.convergeAtTaskId) {
                              convergeLabel =
                                taskLabelById.get(String(gw.convergeAtTaskId)) ||
                                `Task #${gw.convergeAtTaskId}`;
                            }

                            const typeInfo = getGatewayTypeInfo(gw.type);
                            return (
                              <div
                                key={gw.id}
                                style={{
                                  border: `1px solid ${typeInfo.border}`,
                                  borderRadius: 10,
                                  padding: 14,
                                  background: typeInfo.bg,
                                }}
                              >
                                {/* Gateway card header — icon style matches GatewaySection gateway cards */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 12,
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 8,
                                      fontWeight: 700,
                                      color: "#0f172a",
                                    }}
                                  >
                                    {/* Gateway type symbol badge — matches BPMN module type cards */}
                                    <span
                                      style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: 8,
                                        background: typeInfo.color,
                                        color: "white",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 17,
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {typeInfo.symbol}
                                    </span>
                                    {/* Sequence number badge */}
                                    <span
                                      style={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: 6,
                                        background: "rgba(0,0,0,0.08)",
                                        color: "#374151",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: 11,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {gwIdx + 1}
                                    </span>
                                    {gw.name || typeInfo.label + " Gateway"}
                                  </span>
                                  {/* Type pill */}
                                  <span
                                    style={{
                                      background: typeInfo.color,
                                      color: "white",
                                      padding: "3px 10px",
                                      borderRadius: 9999,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      letterSpacing: "0.03em",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {typeInfo.label}
                                  </span>
                                </div>
                                <div
                                  className="stack-on-phone"
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 12,
                                  }}
                                >
                                  <div>
                                    <div className="detail-field-label">
                                      After Task/Gateway/Event
                                    </div>
                                    <div
                                      style={{
                                        padding: "8px 10px",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 6,
                                        background: "#fff",
                                      }}
                                    >
                                      {afterLabel}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="detail-field-label">
                                      Name
                                    </div>
                                    <div
                                      style={{
                                        padding: "8px 10px",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 6,
                                        background: "#fff",
                                      }}
                                    >
                                      {gw.name || "-"}
                                    </div>
                                  </div>
                                </div>

                                {(convergeLabel || gw.convergeGatewayName) && (
                                  <div
                                    className="stack-on-phone"
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: gw.convergeGatewayName
                                        ? "1fr 1fr"
                                        : "1fr",
                                      gap: 12,
                                      marginTop: 12,
                                    }}
                                  >
                                    {convergeLabel && (
                                      <div>
                                        <div className="detail-field-label">
                                          Converge / Synchronize At
                                        </div>
                                        <div
                                          style={{
                                            padding: "8px 10px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: 6,
                                            background: "#fff",
                                          }}
                                        >
                                          {convergeLabel}
                                        </div>
                                      </div>
                                    )}
                                    {gw.convergeGatewayName && (
                                      <div>
                                        <div className="detail-field-label">
                                          Join Gateway Label
                                        </div>
                                        <div
                                          style={{
                                            padding: "8px 10px",
                                            border: "1px solid #e5e7eb",
                                            borderRadius: 6,
                                            background: "#fff",
                                          }}
                                        >
                                          {gw.convergeGatewayName}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div
                                  style={{
                                    marginTop: 12,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    padding: 12,
                                    background: "white",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <div
                                      className="detail-field-label"
                                      style={{ margin: 0 }}
                                    >
                                      Branches
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 10,
                                    }}
                                  >
                                    {(gw.branches || []).map((br, idx) => {
                                      let targetLabel = "-";
                                      if (
                                        typeof br.targetTaskId === "string" &&
                                        br.targetTaskId.startsWith("__GW__")
                                      ) {
                                        targetLabel = `▷ Gateway: ${br.targetGatewayName || "Gateway"}`;
                                      } else if (br.targetTaskId) {
                                        targetLabel =
                                          taskLabelById.get(String(br.targetTaskId)) ||
                                          "-";
                                      } else if (br.endEventName) {
                                        targetLabel = `⏹ End Event: ${br.endEventName}`;
                                      } else {
                                        targetLabel = "⏹ End Event (shared End)";
                                      }
                                      return (
                                        <div
                                          key={br.id || idx}
                                          style={{
                                            background: "#fff",
                                            border: `1px solid ${typeInfo.border}`,
                                            borderRadius: 8,
                                            padding: "10px 12px",
                                          }}
                                        >
                                          {/* Branch header — branch-number badge matches GatewaySection branch-card */}
                                          <div style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            marginBottom: 8,
                                          }}>
                                            <span style={{
                                              width: 22,
                                              height: 22,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: typeInfo.color,
                                              color: "white",
                                              borderRadius: 6,
                                              fontSize: 11,
                                              fontWeight: 700,
                                            }}>
                                              {idx + 1}
                                            </span>
                                            <span style={{ fontWeight: 600, color: "#334155", fontSize: 13 }}>
                                              Branch {idx + 1}
                                            </span>
                                            {br.isDefault && (
                                              <span style={{
                                                marginLeft: "auto",
                                                padding: "2px 8px",
                                                borderRadius: 9999,
                                                background: "#ecfeff",
                                                border: "1px solid #e0f2fe",
                                                color: "#0891b2",
                                                fontSize: 11,
                                                fontWeight: 600,
                                              }}>
                                                Default
                                              </span>
                                            )}
                                          </div>
                                          <div className="stack-on-phone" style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: 8,
                                          }}>
                                            <div>
                                              <div className="detail-field-label">Target</div>
                                              <div style={{
                                                padding: "7px 10px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: 6,
                                                background: "#f8fafc",
                                                fontSize: 13,
                                              }}>
                                                {targetLabel}
                                              </div>
                                            </div>
                                            <div>
                                              <div className="detail-field-label">Condition</div>
                                              <div style={{
                                                padding: "7px 10px",
                                                border: "1px solid #e5e7eb",
                                                borderRadius: 6,
                                                background: "#f8fafc",
                                                fontSize: 13,
                                                fontFamily: "monospace",
                                                color: br.condition ? "#0f172a" : "#9ca3af",
                                              }}>
                                                {br.condition || "—"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {(gw.branches || []).length === 0 && (
                                      <div style={{ color: "#94a3b8", fontSize: 13 }}>
                                        No branches configured
                                      </div>
                                    )}
                                  </div>

                                  {/* Endpoints Section */}
                                  {(gw.endpoints || []).length > 0 && (
                                    <div
                                      style={{
                                        marginTop: 16,
                                        padding: 12,
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 6,
                                        background: "white",
                                      }}
                                    >
                                      <div
                                        className="detail-field-label"
                                        style={{ marginBottom: 12 }}
                                      >
                                        End Events
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 10,
                                        }}
                                      >
                                        {(gw.endpoints || []).map((ep, idx) => {
                                          const endTaskLabel =
                                            (ep.endTaskId &&
                                              taskLabelById.get(
                                                String(ep.endTaskId),
                                              )) ||
                                            "-";
                                          const isExclusive =
                                            (gw.type || "").toLowerCase() ===
                                            "exclusive";
                                          return (
                                            <div
                                              className="stack-on-phone"
                                              key={ep.id || idx}
                                              style={{
                                                display: "grid",
                                                gridTemplateColumns:
                                                  isExclusive ? "1.2fr 1fr 1fr"
                                                  : "1.2fr 1fr",
                                                gap: 8,
                                                alignItems: "center",
                                              }}
                                            >
                                              <div>
                                                <div className="detail-field-label">
                                                  End Task
                                                </div>
                                                <div
                                                  style={{
                                                    padding: "8px 10px",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: 6,
                                                    background: "#fff",
                                                  }}
                                                >
                                                  {endTaskLabel}
                                                </div>
                                              </div>
                                              <div>
                                                <div className="detail-field-label">
                                                  End Event Name
                                                </div>
                                                <div
                                                  style={{
                                                    padding: "8px 10px",
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: 6,
                                                    background: "#fff",
                                                  }}
                                                >
                                                  {ep.endEventName || "-"}
                                                </div>
                                              </div>
                                              {isExclusive && (
                                                <div>
                                                  <div className="detail-field-label">
                                                    Condition
                                                  </div>
                                                  <div
                                                    style={{
                                                      padding: "8px 10px",
                                                      border:
                                                        "1px solid #e5e7eb",
                                                      borderRadius: 6,
                                                      background: "#fff",
                                                    }}
                                                  >
                                                    {ep.condition || "-"}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      }
                    </div>
                  </div>
                ),
              },
              {
                id: "raci",
                label: "RACI Matrix",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 4, fontWeight: 600 }}
                    >
                      RACI Matrix
                    </div>
                    <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                      Read-only. RACI roles are assigned per Task/Job in the Task/Job
                      module.
                    </p>

                    {!raciRollup.hasAssignments ? (
                      <div className="no-results">
                        No RACI assignments found for this process. Open each
                        Task and assign RACI roles to Jobs first.
                      </div>
                    ) : (
                      <div className="raci-flat-wrap">
                        <table className="raci-flat-table">
                          <thead>
                            <tr>
                              <th className="raci-flat-th-task">Task Name</th>
                              <th className="raci-flat-th-job">Job Name</th>
                              <th className="raci-flat-th-code">Job Code</th>
                              {[
                                { role: "R", label: "Responsible" },
                                { role: "A", label: "Accountable" },
                                { role: "C", label: "Consulted" },
                                { role: "I", label: "Informed" },
                              ].map(({ role, label }) => (
                                <th key={role} className="raci-flat-th-role">
                                  <div className="raci-flat-role-header">
                                    <span
                                      className={`raci-legend-badge raci-${role.toLowerCase()}`}
                                    >
                                      {role}
                                    </span>
                                    <span className="raci-flat-role-label">
                                      {label}
                                    </span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {raciRollup.taskGroups.flatMap((tg, tgIdx) => {
                              const isAlt = tgIdx % 2 !== 0;
                              if (tg.isChildProcess) {
                                const open =
                                  !!expandedSubRaci[tg.childProcessId];
                                const out = [
                                  <tr
                                    key={tg.task_id}
                                    className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""} raci-flat-row-first`}
                                  >
                                    <td className="raci-flat-task-cell">
                                      <div className="raci-flat-task-inner">
                                        <span className="raci-flat-step">
                                          {tg.order}
                                        </span>
                                        <div className="raci-flat-task-text">
                                          <span className="raci-flat-task-name">
                                            [Sub-Process] {tg.task_name}
                                          </span>
                                          {tg.task_code && (
                                            <span className="raci-flat-task-code">
                                              [{tg.task_code}]
                                            </span>
                                          )}
                                          <div className="raci-sub-pills">
                                            <SubprocessBadge />
                                            <button
                                              type="button"
                                              className={`raci-sub-toggle${open ? " open" : ""}`}
                                              onClick={() =>
                                                toggleSubRaci(
                                                  tg.childProcessId,
                                                )
                                              }
                                              title={
                                                open
                                                  ? "Hide the sub-process's own RACI"
                                                  : "Show the sub-process's own RACI (who does this work)"
                                              }
                                            >
                                              {open
                                                ? "▾ Hide RACI"
                                                : "▸ View RACI"}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td
                                      colSpan={6}
                                      className="raci-flat-no-jobs"
                                    >
                                      Sub-process — staffed inside its own
                                      process. Use &ldquo;View RACI&rdquo; to
                                      see who does this work.
                                    </td>
                                  </tr>,
                                ];
                                if (open) {
                                  out.push(
                                    <SubprocessRaciDetailRow
                                      key={`${tg.task_id}-detail`}
                                      colSpan={7}
                                      childProcessId={tg.childProcessId}
                                      entry={
                                        subRaciByProcess[tg.childProcessId]
                                      }
                                      onRetry={retrySubRaci}
                                    />,
                                  );
                                }
                                return out;
                              }
                              const rows =
                                tg.jobs.length > 0 ? tg.jobs : [null];
                              return rows.map((job, jIdx) => (
                                <tr
                                  key={
                                    job ?
                                      `${tg.task_id}-${job.job_id}`
                                    : `empty-${tg.task_id}`
                                  }
                                  className={`raci-flat-row${isAlt ? " raci-flat-row-alt" : ""}${jIdx === 0 ? " raci-flat-row-first" : ""}`}
                                >
                                  {jIdx === 0 && (
                                    <td
                                      rowSpan={rows.length}
                                      className="raci-flat-task-cell"
                                    >
                                      <div className="raci-flat-task-inner">
                                        <span className="raci-flat-step">
                                          {tg.order}
                                        </span>
                                        <div className="raci-flat-task-text">
                                          <span className="raci-flat-task-name">
                                            {tg.task_name}
                                          </span>
                                          {tg.task_code && (
                                            <span className="raci-flat-task-code">
                                              [{tg.task_code}]
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  )}
                                  {job ? (
                                    <>
                                      <td className="raci-flat-job-name">
                                        {job.name}
                                      </td>
                                      <td className="raci-flat-job-code">
                                        {job.code || "—"}
                                      </td>
                                      {["R", "A", "C", "I"].map((role) => (
                                        <td
                                          key={role}
                                          className="raci-flat-role-cell"
                                        >
                                          {job.role === role ? (
                                            <span
                                              className={`raci-badge raci-${role.toLowerCase()}`}
                                            >
                                              {role}
                                            </span>
                                          ) : (
                                            <span className="raci-summary-empty">
                                              —
                                            </span>
                                          )}
                                        </td>
                                      ))}
                                    </>
                                  ) : (
                                    <td
                                      colSpan={6}
                                      className="raci-flat-no-jobs"
                                    >
                                      No jobs assigned to this task
                                    </td>
                                  )}
                                </tr>
                              ));
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="raci-legend" style={{ marginTop: 20 }}>
                      {[
                        { role: "R", label: "Responsible", desc: "does the work" },
                        { role: "A", label: "Accountable", desc: "owns outcome" },
                        { role: "C", label: "Consulted", desc: "input needed" },
                        { role: "I", label: "Informed", desc: "kept updated" },
                      ].map(({ role, label, desc }) => (
                        <div className="raci-legend-item" key={role}>
                          <span
                            className={`raci-legend-badge raci-${role.toLowerCase()}`}
                          >
                            {role}
                          </span>
                          {label} &mdash; {desc}
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              },
              {
                id: "kpis",
                label: "KPIs",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 16, fontWeight: 600 }}
                    >
                      Process KPIs
                    </div>

                    {kpiLoading ?
                      <div className="no-results">Loading KPIs...</div>
                    : processKPIs.length === 0 ?
                      <div className="no-results">
                        No KPIs linked to this process.
                      </div>
                    : <div className="kpi-table-container">
                        <table className="kpi-table">
                          <thead>
                            <tr>
                              <th>KPI Name</th>
                              <th>Type</th>
                              <th>Value</th>
                              <th>Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processKPIs.map((kpi) => (
                              <tr key={kpi.id}>
                                <td className="kpi-name-cell">
                                  {getKPIDisplayName(kpi)}
                                </td>
                                <td>
                                  <span
                                    className={`type-badge type-${kpi.kpiType?.toLowerCase()}`}
                                  >
                                    {kpi.kpiType === "TYPE2" ?
                                      "Custom"
                                    : "Standard"}
                                  </span>
                                </td>
                                <td>
                                  <span className="value-badge">
                                    {kpi.value}
                                  </span>
                                </td>
                                <td>
                                  {getKPIUoM(kpi) ?
                                    <span className="uom-badge">
                                      {getKPIUoM(kpi)}
                                    </span>
                                  : <span style={{ color: "#9ca3af" }}>-</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    }
                  </div>
                ),
              },
              {
                id: "paths",
                label: "Path View",
                content: (
                  <ProcessPathsTab
                    processId={processId || proc?.process_id}
                    processName={proc?.process_name || proc?.name || ""}
                    isActive={activeTab === "paths"}
                  />
                ),
              },
              {
                id: "value-added",
                label: "Value-Added",
                content: (
                  <ValueAddedPanel
                    processId={processId || proc?.process_id}
                    isActive={activeTab === "value-added"}
                    readOnly
                  />
                ),
              },
              {
                id: "performance",
                label: "Performance",
                content: (
                  <PerformancePanel
                    processId={processId || proc?.process_id}
                    isActive={activeTab === "performance"}
                    readOnly
                  />
                ),
              },
              {
                id: "flow-analysis",
                label: "Flow Analysis",
                content: (
                  <FlowAnalysisPanel
                    processId={processId || proc?.process_id}
                    isActive={activeTab === "flow-analysis"}
                    readOnly
                  />
                ),
              },
              {
                id: "redesign-scenarios",
                label: "Redesign Scenarios",
                content: (
                  <RedesignScenariosPanel
                    processId={processId || proc?.process_id}
                    isActive={activeTab === "redesign-scenarios"}
                    readOnly
                  />
                ),
              },
              {
                id: "job-capi",
                label: "Job CAPI",
                content: (
                  <JobCapiPanel
                    processId={processId || proc?.process_id}
                    isActive={activeTab === "job-capi"}
                    readOnly
                  />
                ),
              },
            ]}
          />
        }
      </div>
    </div>
  );
};

export default ProcessViewPage;
